import { useCallback, useEffect, useRef, useState } from 'react';
import { useFocusEffect } from 'expo-router';
import { filesApi } from '@/lib/api/files';
import { useFileStore } from '@/store/file';

const EMPTY_PAGE = { page: 1, total: 0, hasMore: false };

export default function useFilesLibrary({ query, category, source, sort, enabled }) {
  const [debouncedQuery, setDebouncedQuery] = useState(query.trim());
  const [ids, setIds] = useState([]);
  const [pagination, setPagination] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const request = useRef(0);
  const morePending = useRef(false);
  const libraryRevision = useFileStore(state => state.libraryRevision || 0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const refresh = useCallback(async () => {
    const version = ++request.current;
    morePending.current = false;
    setLoadingMore(false);
    setLoading(true);
    setError(null);
    // Never label the previous scope's files as results for a failed filter.
    setIds([]);
    setPagination(EMPTY_PAGE);
    if (!enabled) {
      setLoading(false);
      return;
    }
    try {
      const result = await filesApi.fetchLibrary({ query: debouncedQuery, category, source, sort });
      if (version !== request.current) return;
      useFileStore.getState().actions.addFiles(result.files);
      setIds([...new Set(result.files.map(file => file.id))]);
      setPagination(result.pagination || EMPTY_PAGE);
    } catch (err) {
      if (version === request.current) setError(err.message || 'Unable to load files. Please try again.');
    } finally {
      if (version === request.current) setLoading(false);
    }
  }, [debouncedQuery, category, source, sort, enabled]);

  useFocusEffect(useCallback(() => {
    // Refresh after uploads, renames, and deletes, including actions in modals.
    void libraryRevision;
    void refresh();
    return () => { request.current += 1; };
  }, [refresh, libraryRevision]));

  const loadMore = useCallback(async () => {
    if (!enabled || query.trim() !== debouncedQuery || morePending.current || loading || !pagination.hasMore) return;
    morePending.current = true;
    const version = request.current;
    setLoadingMore(true);
    setError(null);
    try {
      const result = await filesApi.fetchLibrary({ query: debouncedQuery, category, source, sort, page: pagination.page + 1 });
      if (version !== request.current) return;
      useFileStore.getState().actions.addFiles(result.files);
      setIds(previous => [...new Set([...previous, ...result.files.map(file => file.id)])]);
      setPagination(result.pagination || EMPTY_PAGE);
    } catch (err) {
      if (version === request.current) setError(err.message || 'Unable to load more files. Please try again.');
    } finally {
      if (version === request.current) {
        morePending.current = false;
        setLoadingMore(false);
      }
    }
  }, [query, debouncedQuery, category, source, sort, pagination, loading, enabled]);

  return { ids, pagination, loading: loading || query.trim() !== debouncedQuery, loadingMore, error, refresh, loadMore };
}
